/* eslint prefer-promise-reject-errors: 0 */
import axios from 'axios';
import { createIntl, createIntlCache } from 'react-intl';
import { Subject } from 'rxjs';
import { getAgentPorts, getNewMessage, getNewState, getPorts } from '../actions/bloquesAction';
import { getLocale } from '../config/locale';
import { REASON } from './usbSerialNumber';
import SocketAgent from './SocketAgent';
import WebSerialAgent from './webSerialAgent';
import WebUSBAgent from './webUSBAgent';
import WebESPAgent from './webESPAgent';
import { getBuildTarget, isESPBoard } from './buildTargets';
import { boardSupportsFirmware } from './traceability/boardModel';
import { embedFirmware, hasFirmware } from './traceability/firmwareSketch';
import { recordFirmwareUpload } from './traceability/firmwareUploads';

/**
 * El código tal como se manda a compilar. El firmware de trazabilidad se embebe
 * acá, en el último paso antes del builder, y no antes: así lo que el usuario ve
 * y lo que se guarda -- el proyecto, la cola de la sesión remota -- queda sin él,
 * y todas las pantallas que cargan código pasan por el mismo lugar.
 */
// En Electron no: el arduino-cli local no trae ProtocoloEDU, que todavía está en desarrollo.
const withFirmware = (code, board) => (!process.env.IS_ELECTRON && boardSupportsFirmware(board) ? embedFirmware(code) : code);

// There is one Agent per tab and each one only sees the ports of its own
// transport, so every Agent publishes its ports under its own source and keeps it
// for its whole life, even when the underlying implementation is swapped.
let portSourceCount = 0;
const nextPortSource = () => {
  portSourceCount += 1;
  return `agent-${portSourceCount}`;
};

class Agent {
  /**
   * `traceability` enciende el puerto compartido: el monitor deja de abrir el
   * puerto para él solo y pasa a ser un suscriptor más de serialSession, que es
   * lo que permite que el ping del protocolo Educabot conviva con él.
   *
   * Va apagado por defecto y hoy solo lo pide BlocklyPanel. Las demás pantallas
   * que construyen un Agent (machinelearning, creabots) siguen con el camino de
   * siempre, sin cambio de comportamiento.
   */
  constructor(store, board, { traceability = false } = {}) {
    this.store = store;
    this.board = board;
    this.traceability = traceability;
    this.portSource = nextPortSource();
    this.platform = 'Windows';
    this.socketElectronConnection = 'ws://localhost:3000';
    this.httpElectronConnection = 'http://localhost:3001';
    this.serialMonitorMessages = new Subject();

    const { locale, intlMessages } = getLocale();
    this.intl = createIntl({ locale, messages: intlMessages }, createIntlCache());

    if (this.board.compilerBoard === 'microbit') {
      this.agent = new WebUSBAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource);
      this.agentType = 'usb';
    } else if (!process.env.IS_ELECTRON && isESPBoard(this.board.compilerBoard) && 'serial' in navigator) {
      this.agent = new WebESPAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource, { sharedPort: this.traceability, identity: this.traceability });
      this.agentType = 'esp';
    } else {
      this.agent = new SocketAgent(this.store, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource);
      this.agentType = 'socket';
    }
    this.intervalConnectAgent = null;
  }

  checkSocket = () => {
    if (!process.env.IS_ELECTRON && !this.agent?.state?.socket && !this.agent.MICROBIT_PRODUCT_ID && 'serial' in navigator) {
      this.agent.stopCheckingAgent();
      if (isESPBoard(this.board.compilerBoard)) {
        this.agent = new WebESPAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource, { sharedPort: this.traceability, identity: this.traceability });
        this.agentType = 'esp';
      } else {
        this.agent = new WebSerialAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource, { sharedPort: this.traceability, identity: this.traceability });
        this.agentType = 'serial';
      }
    }
    // console.log('===============agent', this.agentType, this.platform, process.env.NODE_ENV);
  }

  /**
   * El objeto SerialPort detrás del nombre que muestra el selector. Lo necesita
   * la trazabilidad para abrir su sesión sobre el mismo puerto que usa el resto
   * de la app; los agentes que no manejan puertos del browser no tienen ninguno
   * que dar.
   */
  getSerialPort = (name = '') => (typeof this.agent?.getSerialPortByName === 'function'
    ? this.agent.getSerialPortByName(name)
    : null)

  /**
   * El serial del descriptor usb de una placa ya vinculada, para la máquina de
   * identidad. Con `prompt` abre el selector de WebUSB, y necesita venir de un
   * click; sin él lo lee de los permisos ya dados y no muestra nada.
   */
  readUsbSerialFor = (port, options) => (typeof this.agent?.readUsbSerialFor === 'function'
    ? this.agent.readUsbSerialFor(port, options)
    : Promise.resolve(null))

  /**
   * Le pasa al agente lo que la máquina de identidad averiguó de una placa. Es lo
   * que suelta su evento de conexión, que se retenía justamente esperando esto.
   *
   * Los agentes que no manejan puertos del browser no la implementan, y ahí no
   * hay nada que soltar: sus placas se reportan como siempre.
   */
  setBoardIdentity = (port, identity) => (typeof this.agent?.setBoardIdentity === 'function'
    ? this.agent.setBoardIdentity(port, identity)
    : false)

  /**
   * Si sobre este agente se puede hablar el protocolo Educabot.
   *
   * Los dos agentes de puerto del browser: comparten el puerto con serialSession,
   * que es lo que hace falta para que el ping conviva con el monitor y con el
   * flasheo. Quedan afuera el de micro:bit, cuyo transporte es WebUSB y todavía no
   * tiene firmware de trazabilidad, y el de escritorio (SocketAgent), que habla
   * con la placa a través del plugin y necesita un adaptador propio.
   */
  supportsTraceability = () => this.traceability && (this.agentType === 'esp' || this.agentType === 'serial')

  connectNewBoard = () => {
    if (this.agentType === 'serial' || this.agentType === 'usb' || this.agentType === 'esp') {
      this.agent.connectNewBoard();
    }
  }

  /**
   * Reads the serial number of the board being linked. Every agent implements it,
   * so there is no need to ask which transport is active.
   *
   * Answers with a REASON: 'none' once a serial is in hand, 'absent' when the
   * board was identified and its descriptor carries none, and 'unavailable' when
   * nothing could be read. Only the last one is worth trying again.
   *
   * No port is passed on when the caller named none, rather than a null: the
   * agents default the argument to the port linked last, and a null would be an
   * argument and override that.
   *
   * `unfiltered` opens the chooser without narrowing it to the ids of the boards,
   * for the board whose ids have not been collected yet and which the filter
   * therefore hides.
   */
  fillBoardSerialNumber = (port, options) => {
    if (!this.agent || typeof this.agent.fillBoardSerialNumber !== 'function') {
      return Promise.resolve(REASON.UNAVAILABLE);
    }
    return Promise.resolve(this.agent.fillBoardSerialNumber(port, options)).catch(() => REASON.UNAVAILABLE);
  }

  forceAgent = (type = 'socket') => {
    this.agent.stopCheckingAgent();
    switch (type) {
      case 'serial':
        this.agent = new WebSerialAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource, { sharedPort: this.traceability, identity: this.traceability });
        break;
      case 'usb':
        this.agent = new WebUSBAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource);
        break;
      case 'esp':
        this.agent = new WebESPAgent(this.store, this.board, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource, { sharedPort: this.traceability, identity: this.traceability });
        break;
      case 'socket':
      default:
        this.agent = new SocketAgent(this.store, this.socketElectronConnection, this.httpElectronConnection, this.serialMonitorMessages, this.intl, this.portSource);
        this.agent.disconnectSocket();
        break;
    }
    this.agentType = type;
  }

  setPlatform = (platform, timeout = 4000) => {
    this.platform = platform;
    if (!this.intervalConnectAgent) {
      this.intervalConnectAgent = setTimeout(() => {
        this.checkSocket();
        clearTimeout(this.intervalConnectAgent);
        this.intervalConnectAgent = null;
      }, timeout);
    }
  };

  disconnectSocket = () => this.agent.disconnectSocket();

  stopCheckingAgent = () => this.agent.stopCheckingAgent();

  findPorts = () => {
    if (this.agent) {
      this.agent.findPorts();
    }
  }

  // The source the active implementation publishes under. It is not always this
  // Agent's own source: the socket agent is a singleton shared by every tab and
  // reports every port of the system under a single fixed source.
  getPortSource = () => this.agent?.portSource || this.portSource;

  // Drops this Agent's contribution to the port list, so a closed tab stops
  // showing its boards. Called when the panel that owns the Agent goes away.
  releasePorts = () => {
    this.store.dispatch(getPorts([], this.portSource));
    this.store.dispatch(getAgentPorts([], this.portSource));
  }

  closeMonitor = (port = '') => this.agent.closeMonitor(port);

  openMonitor = (port = '', baudios) => this.agent.openMonitor(port, baudios);

  /**
   * Devuelve el código que se mandó a compilar de verdad, con el firmware si la
   * placa lo lleva, para que el evento de la carga reporte eso mismo.
   */
  uploadToPort = (code = '', board = '', port = '') => {
    const sent = withFirmware(code, board);
    if (hasFirmware(sent)) recordFirmwareUpload(port, sent);
    this.agent.uploadToPort(sent, board, port);
    return sent;
  };

  changeBaudios = (port = '', baudios = 0) => {
    this.closeMonitor(port);
    this.openMonitor(port, baudios);
  };

  sendMonitor = (port = '', command = '') => this.agent.sendMonitor(port, command);

  uploadRemoteSession = (hex = '', board = '', port = '') => this.agent.uploadRemoteSession(hex, board, port);

  compileRemoteSession = (code = '', board = '') => new Promise((resolve, reject) => {
    this.store.dispatch(getNewState('#### UPLOAD TO PORT'));
    /**
     * Response object
     * status: Possible values are SUCCESS, ERROR.
     * message: Error message, if there is any.
     */
    const selectedBoard = board.compilerBoard || 'arduino';
    if (!board) {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.boardMustBeSelected' })));
      reject('Debes seleccionar una placa');
    }

    this.store.dispatch(getNewMessage(this.intl.formatMessage({ id: 'agent.codeIsCompiling' })));


    const { uri: buildURI, params: buildParams } = getBuildTarget(selectedBoard, withFirmware(code, board), this.httpElectronConnection);

    this.store.dispatch(getNewState('#### UPLOAD TO EB_BUILD'));

    axios.post(
      buildURI,
      buildParams,
      {
        withCredentials: false,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': '*/*', //eslint-disable-line
        },
      },
    ).then((response) => {
      let hex;
      if (isESPBoard(selectedBoard)) {
        hex = response.data.payload?.parts ? JSON.stringify({ parts: response.data.payload.parts }) : null;
      } else {
        hex = (selectedBoard === 'microbit') ? response.data.payload.hexMbcodal : response.data.payload.hex;
      }
      if (response.data.status === 'SUCCESS' && hex) {
        this.store.dispatch(getNewState('#### FINISH AGENT'));
        resolve(hex);
      } else {
        this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
        this.store.dispatch(getNewMessage(response.data.message));
        reject(response.data.message);
      }
    }).catch((responseError) => {
      this.store.dispatch(getNewState('#### ERROR EB_BUILD'));
      // store.dispatch(getNewMessage(responseError));
      reject(responseError);
    });
  });
}

export default Agent;
